import React, { useState } from 'react';
import { Card, FigmaLogo } from '../ui';
import { account, templateSchemas, DEFAULT_TEMPLATE_SCHEMA } from '../copData';
import { useCop } from '../CopApp';

// Catalog of every creative field. A template's schema picks the relevant
// subset (in order), so changing the template changes which inputs appear.
const CONTENT_FIELDS = {
	eyeline: { label: 'Eyeline', ph: 'Get 40% off your order', hint: 'max 30 characters' },
	headline: { label: 'Headline', ph: "Wait! Don't leave yet...", hint: 'max 30 characters' },
	subtext: { label: 'Body text', ph: 'A little more detail…', hint: 'optional', span: true },
	question: { label: 'Question', ph: 'What brought you in today?', hint: 'survey prompt', span: true },
	options: { label: 'Answer options', ph: 'Just browsing, Ready to buy', hint: 'comma-separated', span: true },
	inputPlaceholder: { label: 'Email field placeholder', ph: 'you@email.com', hint: 'lead capture' },
	buttonText: { label: 'Button text', ph: 'Claim Offer', hint: 'max 2 words' },
	imageUrl: { label: 'Image URL', ph: 'https://example.com/image.jpg', hint: 'optional', span: true },
	coupon: { label: 'Coupon code', ph: 'SAVE10', hint: 'optional' },
};

// CSS controls grouped by what they affect. A schema picks the relevant groups.
const STYLE_GROUP_DEFS = {
	panel: {
		title: 'Panel',
		fields: [
			['campaignRadius', 'Corner radius', 'text'],
			['campaignPadding', 'Padding', 'text'],
			['textColor', 'Text color', 'color'],
			['gradientStart', 'Gradient start', 'color'],
			['gradientEnd', 'Gradient end', 'color'],
			['imageOverlayOpacity', 'Image overlay opacity', 'text'],
			['imagePanelOpacity', 'Image panel opacity', 'text'],
		],
	},
	headline: {
		title: 'Headline & eyeline',
		fields: [
			['headlineSize', 'Headline size', 'text'],
			['eyelineSize', 'Eyeline size', 'text'],
		],
	},
	button: {
		title: 'Button',
		fields: [
			['buttonBackground', 'Background', 'color'],
			['buttonTextSize', 'Text size', 'text'],
			['buttonRadius', 'Radius', 'text'],
			['buttonPadding', 'Padding', 'text'],
		],
	},
	close: { title: 'Close icon', fields: [['closeIconSize', 'Size', 'text']] },
};

const PRODUCTS = [
	{ name: 'Featured', price: '$129' },
	{ name: 'Bestseller', price: '$249' },
	{ name: 'New', price: '$89' },
];

// ---- preview building blocks (styled live from css, filled from creative) ----
const Eyeline = ({ c, css }) => (c.eyeline ? <div className="cop-dz-eyeline" style={{ fontSize: css.eyelineSize, color: css.textColor, opacity: 0.85 }}>{c.eyeline}</div> : null);
const Headline = ({ text, css }) => <div className="cop-dz-headline" style={{ fontSize: css.headlineSize, color: css.textColor }}>{text}</div>;
const Subtext = ({ text, css }) => (text ? <div className="cop-dz-sub" style={{ color: css.textColor, opacity: 0.85 }}>{text}</div> : null);
const Coupon = ({ text, css }) => (text ? <div className="cop-dz-coupon" style={{ color: css.textColor }}>{text}</div> : null);
function Btn({ text, css }) {
	return (
		<button
			className="cop-dz-btn"
			style={{ background: css.buttonBackground, borderRadius: css.buttonRadius, padding: css.buttonPadding, fontSize: css.buttonTextSize }}
			onClick={(e) => e.preventDefault()}
		>
			{text || 'Button'}
		</button>
	);
}

function KindBody({ kind, c, css }) {
	switch (kind) {
		case 'survey':
			return (
				<>
					<Eyeline c={c} css={css} />
					<Headline text={c.question || c.headline} css={css} />
					<div className="cop-dz-options">
						{(c.options || '')
							.split(',')
							.map((o) => o.trim())
							.filter(Boolean)
							.slice(0, 4)
							.map((o) => (
								<span className="cop-dz-opt" key={o} style={{ color: css.textColor }}>{o}</span>
							))}
					</div>
				</>
			);
		case 'lead':
			return (
				<>
					<Eyeline c={c} css={css} />
					<Headline text={c.headline} css={css} />
					<Subtext text={c.subtext} css={css} />
					<div className="cop-dz-field" style={{ color: css.textColor }}>{c.inputPlaceholder || 'you@email.com'}</div>
					<Btn text={c.buttonText} css={css} />
					<Coupon text={c.coupon} css={css} />
				</>
			);
		case 'recs':
			return (
				<>
					<Headline text={c.headline} css={css} />
					<Subtext text={c.subtext} css={css} />
					<div className="cop-dz-products">
						{PRODUCTS.map((p) => (
							<div className="cop-dz-product" key={p.name}>
								<span className="cop-dz-thumb" />
								<span style={{ color: css.textColor, fontSize: 10 }}>{p.name}</span>
								<span style={{ color: css.textColor, opacity: 0.85, fontSize: 10 }}>{p.price}</span>
							</div>
						))}
					</div>
					<Btn text={c.buttonText} css={css} />
				</>
			);
		case 'cart':
			return (
				<>
					<Headline text={c.headline} css={css} />
					<div className="cop-dz-cartlist">
						{PRODUCTS.slice(0, 2).map((p) => (
							<div className="cop-dz-cartrow" key={p.name}>
								<span className="cop-dz-thumb sm" />
								<span style={{ flex: 1, color: css.textColor, fontSize: 11, textAlign: 'left' }}>{p.name}</span>
								<span style={{ color: css.textColor, fontSize: 11 }}>{p.price}</span>
							</div>
						))}
					</div>
					<Coupon text={c.coupon} css={css} />
					<Btn text={c.buttonText} css={css} />
				</>
			);
		case 'email-cart':
			return (
				<>
					<Headline text={c.headline} css={css} />
					<Subtext text={c.subtext} css={css} />
					<div className="cop-dz-cartlist">
						{PRODUCTS.slice(0, 2).map((p) => (
							<div className="cop-dz-cartrow" key={p.name}>
								<span className="cop-dz-thumb sm" />
								<span style={{ flex: 1, color: css.textColor, fontSize: 11, textAlign: 'left' }}>{p.name}</span>
								<span style={{ color: css.textColor, fontSize: 11 }}>{p.price}</span>
							</div>
						))}
					</div>
					<Coupon text={c.coupon} css={css} />
					<Btn text={c.buttonText} css={css} />
				</>
			);
		case 'email-recs':
			return (
				<>
					<Headline text={c.headline} css={css} />
					<div className="cop-dz-products">
						{PRODUCTS.map((p) => (
							<div className="cop-dz-product" key={p.name}>
								<span className="cop-dz-thumb" />
								<span style={{ color: css.textColor, fontSize: 10 }}>{p.name}</span>
							</div>
						))}
					</div>
					<Btn text={c.buttonText} css={css} />
				</>
			);
		case 'email-promo':
			return (
				<>
					<Coupon text={c.coupon} css={css} />
					<Headline text={c.headline} css={css} />
					<Subtext text={c.subtext} css={css} />
					<Btn text={c.buttonText} css={css} />
				</>
			);
		case 'email-hero':
			return (
				<>
					<Eyeline c={c} css={css} />
					<Headline text={c.headline} css={css} />
					<Subtext text={c.subtext} css={css} />
					<Btn text={c.buttonText} css={css} />
				</>
			);
		default: // 'offer'
			return (
				<>
					<Eyeline c={c} css={css} />
					<Headline text={c.headline} css={css} />
					<Coupon text={c.coupon} css={css} />
					<Btn text={c.buttonText} css={css} />
				</>
			);
	}
}

function DesignPreview({ kind, creative, css, mobile }) {
	const isEmail = kind.startsWith('email-');
	const panelStyle = {
		borderRadius: css.campaignRadius,
		padding: css.campaignPadding,
		background: `linear-gradient(135deg, ${css.gradientStart}, ${css.gradientEnd})`,
		color: css.textColor,
		maxWidth: mobile ? 200 : 420,
	};
	return (
		<div className="cop-dz-modal" style={panelStyle}>
			{isEmail ? (
				<div className="cop-dz-email-head" style={{ color: css.textColor }}>{account?.merchant_name || 'Your Store'}</div>
			) : (
				<span className="cop-dz-close" style={{ fontSize: css.closeIconSize, color: css.textColor }}>×</span>
			)}
			<KindBody kind={kind} c={creative} css={css} />
		</div>
	);
}

function StyleField({ keyName, label, type, css, onChange }) {
	return (
		<label className="cop-form-field">
			<span>{label}</span>
			{type === 'color' ? (
				<div className="cop-color-field">
					<input type="color" value={css[keyName]} onChange={onChange(keyName)} aria-label={label} />
					<input className="cop-input" value={css[keyName]} onChange={onChange(keyName)} spellCheck={false} />
				</div>
			) : (
				<input className="cop-input" value={css[keyName]} onChange={onChange(keyName)} spellCheck={false} />
			)}
		</label>
	);
}

export default function CampaignDesign({ tpl, creative, setCreative, css, setCss }) {
	const { showToast } = useCop();
	const [mode, setMode] = useState('template'); // 'template' | 'upload'
	const [previewDevice, setPreviewDevice] = useState('desktop');
	const [stylingOpen, setStylingOpen] = useState(false);
	const setText = (key) => (e) => setCreative((c) => ({ ...c, [key]: e.target.value }));
	const setCssField = (key) => (e) => setCss((c) => ({ ...c, [key]: e.target.value }));

	// The selected template's category drives the preview, content, and CSS.
	const schema = templateSchemas[tpl.categoryCode] || DEFAULT_TEMPLATE_SCHEMA;
	const contentKeys = schema.content.filter((k) => CONTENT_FIELDS[k]);
	const styleGroups = schema.style.map((g) => STYLE_GROUP_DEFS[g]).filter(Boolean);

	return (
		<div className="cop-design-split">
			<div className="cop-design-main">
				{/* 1 — pick the design */}
				<Card title="Template" icon="▤" right="choose a template or upload your own">
					<div className="cop-filter-row" style={{ marginBottom: 14 }}>
						{[
							['template', 'Use Template'],
							['upload', 'Upload Design'],
						].map(([code, label]) => (
							<button key={code} className={`cop-filter-chip${mode === code ? ' active' : ''}`} onClick={() => setMode(code)}>
								{label}
							</button>
						))}
					</div>

					{mode === 'upload' ? (
						<div>
							<div className="cop-design-links" style={{ marginBottom: 10 }}>
								<a href="https://www.figma.com/design/ueAXuRs3jlggPcYT8x9yw6/Upsellify-Templates" target="_blank" rel="noreferrer">
									<FigmaLogo size={12} /> Figma Templates
								</a>
								<a href="https://www.figma.com/design/ueAXuRs3jlggPcYT8x9yw6/Upsellify-Templates" target="_blank" rel="noreferrer">Photoshop Templates</a>
							</div>
							<button className="cop-upload-drop" onClick={() => showToast('Upload accepts .psd, .ai, .fig, or .zip')}>
								<div className="t">Click to upload your design</div>
								<div className="s">Accepted files: .psd, .ai, .fig, or .zip</div>
								<span className="cop-btn small" style={{ marginTop: 10 }}>Choose File</span>
							</button>
						</div>
					) : (
						<div>
							<div className="cop-design-toolbar">
								<span className="cop-filter-row">
									{tpl.channels.map((c) => (
										<button key={c.code} className={`cop-filter-chip${tpl.channel === c.code ? ' active' : ''}`} onClick={() => tpl.onChannelChange(c.code)}>
											{c.name}
										</button>
									))}
								</span>
								<select className="cop-select cop-config-select" value={tpl.categoryCode} onChange={(e) => tpl.onCategoryChange(e.target.value)}>
									{tpl.channelCategories.map((c) => (
										<option key={c.code} value={c.code}>{c.name}</option>
									))}
								</select>
							</div>

							<div className="cop-design-sub">Select a design</div>
							<div className="cop-tmpl-grid">
								{tpl.category.templates.map((t) => (
									<button
										key={t.node_id}
										className={`cop-tmpl-card${tpl.templateId === t.node_id ? ' active' : ''}`}
										onClick={() => tpl.onTemplateChange(t.node_id)}
									>
										<span className="nm">{t.name}</span>
										<a
											className="lk"
											href={tpl.linkOverrides[t.node_id] ?? tpl.figmaUrl(t.node_id)}
											target="_blank"
											rel="noreferrer"
											onClick={(e) => e.stopPropagation()}
										>
											<FigmaLogo size={11} /> open
										</a>
									</button>
								))}
							</div>

							<div className="cop-design-divider" />
							<div className="cop-design-sub">Design source</div>
							<div className="cop-figma-row">
								<input
									className="cop-input cop-figma-link-input"
									value={tpl.figmaLink}
									onChange={(e) => tpl.onLinkChange(e.target.value)}
									spellCheck={false}
									aria-label="Figma template link"
								/>
								<div className="cop-figma-actions">
									<button className="cop-btn small" onClick={tpl.openFigma} disabled={!tpl.figmaLink}>
										<FigmaLogo size={13} /> Open in Figma ↗
									</button>
									{tpl.linkEdited && <span className="cop-figma-saved">Saved ✓</span>}
									{tpl.linkEdited && <button className="cop-link" onClick={tpl.resetLink}>Reset</button>}
								</div>
								<div className="cop-figma-links">
									{tpl.selectedTemplateLinks.map((l) => (
										<a
											key={l.channelCode}
											className={`cop-figma-tmpl-link${l.channelCode === tpl.channel ? ' active' : ''}`}
											href={l.url}
											target="_blank"
											rel="noreferrer"
										>
											<FigmaLogo size={12} />
											<span className="ch">{l.channelName}</span>
											<span className="nm">{l.label}</span>
											<span className="go">↗</span>
										</a>
									))}
								</div>
							</div>
						</div>
					)}
				</Card>

				{/* 2 — write the copy (fields adapt to the template) */}
				<Card title="Content" icon="✦" right={`${tpl.category.name} fields`}>
					<div className="cop-form-grid">
						{contentKeys.map((key) => {
							const f = CONTENT_FIELDS[key];
							return (
								<label className={`cop-form-field${f.span ? ' cop-form-span' : ''}`} key={key}>
									<span>{f.label} <em className="cop-field-hint">{f.hint}</em></span>
									<input className="cop-input" value={creative[key]} onChange={setText(key)} placeholder={f.ph} spellCheck={false} />
								</label>
							);
						})}
					</div>
				</Card>

				{/* 3 — fine-tune the look (controls adapt to the template) */}
				<Card
					title="Styling"
					icon="⚙"
					right={
						<button className="cop-link" onClick={() => setStylingOpen((o) => !o)}>
							{stylingOpen ? 'Hide' : 'Customize'} CSS
						</button>
					}
				>
					{stylingOpen ? (
						styleGroups.map((group) => (
							<div className="cop-style-group" key={group.title}>
								<div className="cop-design-sub">{group.title}</div>
								<div className="cop-form-grid">
									{group.fields.map(([key, label, type]) => (
										<StyleField key={key} keyName={key} label={label} type={type} css={css} onChange={setCssField} />
									))}
								</div>
							</div>
						))
					) : (
						<div className="cop-design-collapsed">
							Using default styling for this template. <button className="cop-link" onClick={() => setStylingOpen(true)}>Customize CSS</button> to adjust the
							panel, type, and button.
						</div>
					)}
				</Card>
			</div>

			<div className="cop-design-side">
				<Card
					title="Live Preview"
					icon="▣"
					right={
						<span className="cop-filter-row">
							{['desktop', 'mobile'].map((d) => (
								<button key={d} className={`cop-filter-chip${previewDevice === d ? ' active' : ''}`} onClick={() => setPreviewDevice(d)}>
									{d}
								</button>
							))}
						</span>
					}
				>
					<div className="cop-preview-stage">
						<div className="cop-design-previews" key={schema.kind + tpl.templateId + previewDevice}>
							{previewDevice === 'mobile' ? (
								<div className="cop-dz-phone">
									<DesignPreview kind={schema.kind} creative={creative} css={css} mobile />
								</div>
							) : (
								<DesignPreview kind={schema.kind} creative={creative} css={css} />
							)}
						</div>
					</div>
					<div className="cop-design-preview-meta">
						{tpl.category.name} · {tpl.template.name}
					</div>
				</Card>
			</div>
		</div>
	);
}
